import { createContext, useContext, useState, useCallback } from 'react';

interface Toast {
  id: number;
  message: string;
  type: 'error' | 'success' | 'info';
}

interface ToastContextValue {
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue>({
  showError: () => {},
  showSuccess: () => {},
});

let nextId = 1;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = (id: number) => setToasts(t => t.filter(x => x.id !== id));

  const add = useCallback((message: string, type: Toast['type']) => {
    const id = nextId++;
    setToasts(t => [...t, { id, message, type }]);
    // En kvittering skal bare ses; en fejl skal kunne læses færdig
    setTimeout(() => remove(id), type === 'error' ? 10000 : 3500);
  }, []);

  const showError = useCallback((message: string) => add(message, 'error'), [add]);
  const showSuccess = useCallback((message: string) => add(message, 'success'), [add]);

  // Lille kort øverst til højre i designsprogets farver (kun semantiske tokens) — før en
  // knaldgrøn/rød bjælke over hele skærmens bredde, der ikke passede til resten af appen.
  const accent = {
    error: 'var(--color-error)',
    success: 'var(--color-success)',
    info: 'var(--color-primary)',
  };

  return (
    <ToastContext.Provider value={{ showError, showSuccess }}>
      {children}
      <div className="fixed top-4 right-4 left-4 sm:left-auto z-[100] flex flex-col gap-2 sm:w-80 pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.id}
            role={toast.type === 'error' ? 'alert' : 'status'}
            style={{ borderLeftColor: accent[toast.type] }}
            className="pointer-events-auto bg-[var(--color-surface)] text-[var(--text-primary)] border border-[var(--color-border)] border-l-4 rounded-[var(--radius-card)] shadow-[var(--shadow-card-md)] px-4 py-3 flex items-start gap-3 text-sm">
            <span className="flex-1">{toast.message}</span>
            <button onClick={() => remove(toast.id)} aria-label="Luk"
              className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] text-lg leading-none">×</button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
